import { EnvError } from "./error/commonErrors";

export enum EnvVariables {
  FIREBASE_AUTH_DOMAIN = "FIREBASE_AUTH_DOMAIN",
  FIREBASE_API_KEY = "FIREBASE_API_KEY",
  BACKEND_URL = "BACKEND_URL",
  FRONTEND_SENTRY_DSN = "FRONTEND_SENTRY_DSN",
  FRONTEND_ENABLE_SENTRY = "FRONTEND_ENABLE_SENTRY",
  FRONTEND_SENTRY_CONFIG = "FRONTEND_SENTRY_CONFIG",
  TARGET_ENVIRONMENT_NAME = "TARGET_ENVIRONMENT_NAME",
  SENSITIVE_PERSONAL_DATA_RSA_ENCRYPTION_KEY = "SENSITIVE_PERSONAL_DATA_RSA_ENCRYPTION_KEY",
  SENSITIVE_PERSONAL_DATA_RSA_ENCRYPTION_KEY_ID = "SENSITIVE_PERSONAL_DATA_RSA_ENCRYPTION_KEY_ID",
  FRONTEND_SENSITIVE_DATA_FIELDS = "FRONTEND_SENSITIVE_DATA_FIELDS",
  FRONTEND_LOGIN_CODE = "FRONTEND_LOGIN_CODE",
  GLOBAL_DISABLE_LOGIN_CODE = "GLOBAL_DISABLE_LOGIN_CODE",
  FRONTEND_REGISTRATION_CODE = "FRONTEND_REGISTRATION_CODE",
  FRONTEND_DISABLE_REGISTRATION = "FRONTEND_DISABLE_REGISTRATION",
  GLOBAL_DISABLE_REGISTRATION_CODE = "GLOBAL_DISABLE_REGISTRATION_CODE",
  FRONTEND_ENABLE_METRICS = "FRONTEND_ENABLE_METRICS",
  FRONTEND_METRICS_CONFIG = "FRONTEND_METRICS_CONFIG",
  GLOBAL_ENABLE_CV_UPLOAD = "GLOBAL_ENABLE_CV_UPLOAD",
  FRONTEND_ENABLE_NEW_SESSION = "FRONTEND_ENABLE_NEW_SESSION",
  FRONTEND_FEATURES = "FRONTEND_FEATURES",
  FRONTEND_DISABLE_SOCIAL_AUTH = "FRONTEND_DISABLE_SOCIAL_AUTH",
  FRONTEND_SUPPORTED_LOCALES = "FRONTEND_SUPPORTED_LOCALES",
  FRONTEND_DEFAULT_LOCALE = "FRONTEND_DEFAULT_LOCALE",
  GLOBAL_PRODUCT_NAME = "GLOBAL_PRODUCT_NAME",
  FRONTEND_BROWSER_TAB_TITLE = "FRONTEND_BROWSER_TAB_TITLE",
  FRONTEND_META_DESCRIPTION = "FRONTEND_META_DESCRIPTION",
  FRONTEND_SEO = "FRONTEND_SEO",
  FRONTEND_LOGO_URL = "FRONTEND_LOGO_URL",
  FRONTEND_PARTNER_LOGOS = "FRONTEND_PARTNER_LOGOS",
  FRONTEND_DARK_LOGO_URL = "FRONTEND_DARK_LOGO_URL",
  FRONTEND_FAVICON_URL = "FRONTEND_FAVICON_URL",
  FRONTEND_APP_ICON_URL = "FRONTEND_APP_ICON_URL",
  FRONTEND_CHAT_AVATAR_URL = "FRONTEND_CHAT_AVATAR_URL",
  FRONTEND_THEME_CSS_VARIABLES = "FRONTEND_THEME_CSS_VARIABLES",
  FRONTEND_SKILLS_REPORT_OUTPUT_CONFIG = "FRONTEND_SKILLS_REPORT_OUTPUT_CONFIG",
  FRONTEND_GTM_CONTAINER_ID = "FRONTEND_GTM_CONTAINER_ID",
  FRONTEND_GTM_ENABLED = "FRONTEND_GTM_ENABLED",
  FRONTEND_FAQ_TUTORIAL_VIDEO_URL = "FRONTEND_FAQ_TUTORIAL_VIDEO_URL",
  FRONTEND_ILLUSTRATIONS = "FRONTEND_ILLUSTRATIONS",
  FRONTEND_HIDE_PROGRAM_SKILLS = "FRONTEND_HIDE_PROGRAM_SKILLS",
  GLOBAL_COUNTRY_NAME = "GLOBAL_COUNTRY_NAME",
}

export const requiredEnvVariables = [
  EnvVariables.FIREBASE_AUTH_DOMAIN,
  EnvVariables.FIREBASE_API_KEY,
  EnvVariables.BACKEND_URL,
  EnvVariables.TARGET_ENVIRONMENT_NAME,
  EnvVariables.SENSITIVE_PERSONAL_DATA_RSA_ENCRYPTION_KEY,
  EnvVariables.SENSITIVE_PERSONAL_DATA_RSA_ENCRYPTION_KEY_ID,
  EnvVariables.FRONTEND_SUPPORTED_LOCALES,
  EnvVariables.FRONTEND_DEFAULT_LOCALE,
];

/**
 * Retrieves an environment variable from the global `tabiyaConfig` object.
 *
 * This method provides synchronous access to environment variables defined in `env.js`,
 * which is included via `index.html`. All values in `tabiyaConfig` are expected to be
 * Base64-encoded UTF-8 strings, as produced during the build process (e.g., by
 * `iac/frontend/prepare_frontend.py`).
 *
 * Upon retrieval, this function automatically decodes the Base64 string and interprets it
 * as UTF-8 using `TextDecoder`, ensuring correct handling of Unicode characters.
 *
 * If the variable is missing or an error occurs during decoding, an empty string is returned,
 * and an `EnvError` is logged to the console.
 *
 * Notes:
 * - This function is designed to reverse only the Base64 encoding applied for obfuscation.
 * - It does not attempt to infer or parse the meaning or type of the decoded data.
 *   The calling code is responsible for handling the data appropriately (e.g. parsing JSON,
 *   decoding binary data, converting to numbers, etc.).
 * - Binary content (such as images or files) should be base64-encoded before being written
 *   to the `.env` file. The consumer must then manually convert the decoded string back
 *   into a binary format (e.g. a Blob or Uint8Array).
 *
 * Examples:
 *
 * 1. Unicode Text (e.g., Chinese "好运" = "Good Luck")
 *
 * // Python encoding step (in prepare_frontend.py):
 * base64.b64encode("好运".encode("utf-8"))  # Yields: b'5aSn5a2m'
 *
 * // Decoding in frontend (handled internally by getEnv):
 * const value = getEnv("APP_TITLE");
 * console.log(value); // "好运"
 *
 * 2. Binary Data (e.g., JPEG Image)
 *
 * // Assume a JPEG file was base64-encoded and added to `.env`:
 * MY_IMAGE="base64-encoded-string-of-jpeg"
 *
 * // Retrieve and convert to binary:
 * const base64String = getEnv("MY_IMAGE");
 * const binary = Uint8Array.from(atob(base64String), c => c.charCodeAt(0));
 * const blob = new Blob([binary], { type: "image/jpeg" });
 * const imageUrl = URL.createObjectURL(blob);
 *
 * // Use in an <img> tag:
 * document.getElementById("preview").src = imageUrl;
 *
 * @param key - The name of the environment variable to retrieve.
 * @returns The decoded UTF-8 string, or an empty string if not found or invalid.
 */
export const getEnv = (key: string): string => {
  try {
    const env = (window as any).tabiyaConfig;
    const base64Value = env?.[key];
    if (!base64Value) {
      return "";
    }

    const binary = window.atob(base64Value);
    const bytes = Uint8Array.from(binary, (char) => char.charCodeAt(0));
    return new TextDecoder("utf-8").decode(bytes);
  } catch (e) {
    console.error(new EnvError(`Error loading environment variable ${key}`, e));
    return "";
  }
};

export const getFirebaseDomain = () => {
  return getEnv(EnvVariables.FIREBASE_AUTH_DOMAIN);
};

export const getFirebaseAPIKey = () => {
  return getEnv(EnvVariables.FIREBASE_API_KEY);
};

export const getBackendUrl = () => {
  return getEnv(EnvVariables.BACKEND_URL);
};

export const getSentryDSN = () => {
  return getEnv(EnvVariables.FRONTEND_SENTRY_DSN);
};

export const getSentryConfig = () => {
  return getEnv(EnvVariables.FRONTEND_SENTRY_CONFIG);
};

export const getSentryEnabled = () => {
  return getEnv(EnvVariables.FRONTEND_ENABLE_SENTRY);
};

export const getTargetEnvironmentName = () => {
  return getEnv(EnvVariables.TARGET_ENVIRONMENT_NAME);
};

export const getSensitivePersonalDataRSAEncryptionKey = () => {
  return getEnv(EnvVariables.SENSITIVE_PERSONAL_DATA_RSA_ENCRYPTION_KEY);
};

export const getSensitivePersonalDataRSAEncryptionKeyId = () => {
  return getEnv(EnvVariables.SENSITIVE_PERSONAL_DATA_RSA_ENCRYPTION_KEY_ID);
};

export const getSensitiveDataFields = () => {
  return getEnv(EnvVariables.FRONTEND_SENSITIVE_DATA_FIELDS);
};

export const getApplicationLoginCode = () => {
  return getEnv(EnvVariables.FRONTEND_LOGIN_CODE);
};

export const getApplicationRegistrationCode = () => {
  return getEnv(EnvVariables.FRONTEND_REGISTRATION_CODE);
};

export const getLoginCodeDisabled = () => {
  return getEnv(EnvVariables.GLOBAL_DISABLE_LOGIN_CODE);
};

export const getRegistrationDisabled = () => {
  return getEnv(EnvVariables.FRONTEND_DISABLE_REGISTRATION);
};

export const getRegistrationCodeDisabled = () => {
  return getEnv(EnvVariables.GLOBAL_DISABLE_REGISTRATION_CODE);
};

export const getMetricsEnabled = () => {
  return getEnv(EnvVariables.FRONTEND_ENABLE_METRICS);
};

export const getMetricsConfig = () => {
  return getEnv(EnvVariables.FRONTEND_METRICS_CONFIG);
};

export const getFeatures = () => {
  return getEnv(EnvVariables.FRONTEND_FEATURES);
};

export const getCvUploadEnabled = () => {
  return getEnv(EnvVariables.GLOBAL_ENABLE_CV_UPLOAD);
};

export const getNewSessionEnabled = () => {
  const value = getEnv(EnvVariables.FRONTEND_ENABLE_NEW_SESSION);
  return value.toLowerCase() === "true";
};

export const getSocialAuthDisabled = () => {
  return getEnv(EnvVariables.FRONTEND_DISABLE_SOCIAL_AUTH);
};

export const getSupportedLocales = () => {
  return getEnv(EnvVariables.FRONTEND_SUPPORTED_LOCALES);
};

export const getDefaultLocale = () => {
  return getEnv(EnvVariables.FRONTEND_DEFAULT_LOCALE);
};

export const getProductName = () => {
  const envAppName = getEnv(EnvVariables.GLOBAL_PRODUCT_NAME);
  if (!envAppName) {
    console.warn("Product name not set, keeping the default");
    return "Njila";
  }

  return envAppName;
};

export const getCountryName = () => {
  const envCountryName = getEnv(EnvVariables.GLOBAL_COUNTRY_NAME);
  if (!envCountryName) {
    console.warn("Country name not set, keeping the default");
    return "your country";
  }

  return envCountryName;
};

export const getBrowserTabTitle = () => getEnv(EnvVariables.FRONTEND_BROWSER_TAB_TITLE);

export const getMetaDescription = () => getEnv(EnvVariables.FRONTEND_META_DESCRIPTION);

export const getSeoEnvVar = () => getEnv(EnvVariables.FRONTEND_SEO);

export const getSkillsReportOutputConfigEnvVar = () => getEnv(EnvVariables.FRONTEND_SKILLS_REPORT_OUTPUT_CONFIG);

export const getGtmContainerId = () => getEnv(EnvVariables.FRONTEND_GTM_CONTAINER_ID);

export const getGtmEnabled = () => getEnv(EnvVariables.FRONTEND_GTM_ENABLED);

export const getFaqTutorialVideoUrl = () => getEnv(EnvVariables.FRONTEND_FAQ_TUTORIAL_VIDEO_URL);

export const getProgramSkillsVisibility = () =>
  getEnv(EnvVariables.FRONTEND_HIDE_PROGRAM_SKILLS).toLowerCase() !== "true";

export const getLogoUrl = () => getEnv(EnvVariables.FRONTEND_LOGO_URL);

/**
 * A single partner/branding logo rendered in the application footer.
 *
 * The list of logos is provided through the `FRONTEND_PARTNER_LOGOS` environment variable as a
 * JSON array, allowing the footer logos to be customised per project. `height` and `width` are
 * optional pixel sizes; when omitted the footer applies sensible defaults.
 */
export interface PartnerLogo {
  src: string;
  alt?: string;
  height?: number;
  width?: number;
}

/**
 * Returns the list of partner logos configured via `FRONTEND_PARTNER_LOGOS`.
 *
 * Returns an empty array when the variable is unset, is not a JSON array, or cannot be parsed
 * (an `EnvError` is logged in the latter two cases). Entries without a string `src` are dropped.
 * Callers (e.g. the footer) are expected to provide their own defaults when the list is empty.
 */
export const getPartnerLogos = (): PartnerLogo[] => {
  const jsonString = getEnv(EnvVariables.FRONTEND_PARTNER_LOGOS);
  if (!jsonString) {
    return [];
  }
  try {
    const parsed = JSON.parse(jsonString);
    if (!Array.isArray(parsed)) {
      console.error(new EnvError("FRONTEND_PARTNER_LOGOS must be a JSON array"));
      return [];
    }
    return parsed.filter((logo): logo is PartnerLogo => !!logo && typeof logo.src === "string");
  } catch (e) {
    console.error(new EnvError("Error parsing FRONTEND_PARTNER_LOGOS JSON", e));
    return [];
  }
};

export const getDarkLogoUrl = () => getEnv(EnvVariables.FRONTEND_DARK_LOGO_URL);

export const getFaviconUrl = () => getEnv(EnvVariables.FRONTEND_FAVICON_URL);

export const getAppIconUrl = () => getEnv(EnvVariables.FRONTEND_APP_ICON_URL);

export const getChatAvatarUrl = () => getEnv(EnvVariables.FRONTEND_CHAT_AVATAR_URL);

export interface IllustrationItem {
  src: string;
  width?: number | string;
  height?: number | string;
}

export interface IllustrationUrls {
  loginHero: IllustrationItem;
  loginFeature1: IllustrationItem;
  loginFeature2: IllustrationItem;
  loginFeature3: IllustrationItem;
  homeHero: IllustrationItem;
  homeHeroIllustrationPosition?: "center" | "edge";
  careerReadinessHero: IllustrationItem;
  authShapesBackground: IllustrationItem;
  dashboardShapesBackground?: IllustrationItem;
}

const toIllustrationItem = (val: string | IllustrationItem): IllustrationItem =>
  typeof val === "string" ? { src: val } : val;

export const DEFAULT_ILLUSTRATION_URLS: IllustrationUrls = {
  loginHero: { src: "/climber.svg" },
  loginFeature1: { src: "/conversation.svg" },
  loginFeature2: { src: "/resume.svg" },
  loginFeature3: { src: "/runner.svg" },
  homeHero: { src: "/path.svg" },
  careerReadinessHero: { src: "/thinkers.svg" },
  authShapesBackground: { src: "/Shapes.svg" },
};

export const getIllustrationUrls = (): IllustrationUrls => {
  const jsonString = getEnv(EnvVariables.FRONTEND_ILLUSTRATIONS);
  if (!jsonString) {
    return DEFAULT_ILLUSTRATION_URLS;
  }
  try {
    const parsed = JSON.parse(jsonString) as Partial<Record<keyof IllustrationUrls, string | IllustrationItem>>;
    return {
      ...DEFAULT_ILLUSTRATION_URLS,
      ...Object.fromEntries(
        Object.entries(parsed).map(([key, val]) => [
          key,
          key === "homeHeroIllustrationPosition" ? val : toIllustrationItem(val as string | IllustrationItem),
        ])
      ),
    } as IllustrationUrls;
  } catch (e) {
    console.error(new EnvError("Error parsing FRONTEND_ILLUSTRATIONS JSON", e));
    return DEFAULT_ILLUSTRATION_URLS;
  }
};

export interface ThemeCssVariables {
  "brand-primary"?: string;
  "brand-primary-light"?: string;
  "brand-primary-dark"?: string;
  "brand-primary-contrast-text"?: string;
  "brand-secondary"?: string;
  "brand-secondary-light"?: string;
  "brand-secondary-dark"?: string;
  "brand-secondary-contrast-text"?: string;
  "brand-tertiary"?: string;
  "brand-tertiary-light"?: string;
  "brand-tertiary-dark"?: string;
  "brand-tertiary-contrast-text"?: string;
  "brand-accent"?: string;
  "brand-accent-light"?: string;
  "brand-accent-dark"?: string;
  "brand-accent-contrast-text"?: string;
  "brand-quaternary"?: string;
  "brand-quaternary-light"?: string;
  "brand-quaternary-dark"?: string;
  "brand-quaternary-contrast-text"?: string;
  "brand-neutral"?: string;
  "brand-neutral-light"?: string;
  "brand-neutral-dark"?: string;
  "brand-neutral-contrast-text"?: string;
  "brand-highlight"?: string;
  "brand-highlight-light"?: string;
  "brand-highlight-dark"?: string;
  "brand-highlight-contrast-text"?: string;
  "nav-main-background"?: string;
  "nav-main-text"?: string;
  "page-background"?: string;
  "page-background-light"?: string;
  "page-background-dark"?: string;
  "page-background-contrast-text"?: string;
  "text-primary"?: string;
  "text-secondary"?: string;
  "text-accent"?: string;
  "font-heading"?: string;
  "font-body"?: string;
}

export const getThemeCssVariables = (): ThemeCssVariables => {
  const jsonString = getEnv(EnvVariables.FRONTEND_THEME_CSS_VARIABLES);
  if (!jsonString) {
    return {};
  }

  try {
    return JSON.parse(jsonString) as ThemeCssVariables;
  } catch (e) {
    console.error(new EnvError(`Error parsing FRONTEND_THEME_CSS_VARIABLES JSON`, e));
    return {};
  }
};

export const ensureRequiredEnvVars = () => {
  requiredEnvVariables.forEach((key: EnvVariables) => {
    if (!getEnv(key)) {
      console.warn(`Required environment variable ${key} is not set`);
    }
  });
};
